/**
 * 抽查执行页详情：任务 + 其下全部样本（含 POD 缩略图时效 URL）
 */
import {useCallback, useRef, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {message} from 'antd';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {SpotTaskDetail} from '../types/ExemptSpotCheckType';
import {GET_SPOT_TASK_DETAIL, GetSpotTaskDetailResponse} from '../graphql/queries/spotTaskQueries';
import {toSpotSampleRow, toSpotTaskRow} from '../graphql/spotTaskConverter';

interface UseSpotTaskDetailResult {
    detail?: SpotTaskDetail;
    loading: boolean;
    loadDetail: (taskId: number) => Promise<void>;
    clearDetail: () => void;
}

export const useSpotTaskDetail = (): UseSpotTaskDetailResult => {
    const client = useApolloClient();
    const [detail, setDetail] = useState<SpotTaskDetail | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    /** 请求序号：加载期间再点别的行会并发两次请求，后到的旧响应会让抽屉显示非当前行的任务 */
    const requestSeq = useRef(0);

    const loadDetail = useCallback(
        async (taskId: number) => {
            const seq = ++requestSeq.current;
            setLoading(true);
            try {
                const res = await client.query<GetSpotTaskDetailResponse>({
                    query: GET_SPOT_TASK_DETAIL,
                    variables: {taskId},
                    // 判定后需拿到最新进度，不能吃缓存
                    fetchPolicy: 'network-only',
                });
                if (seq !== requestSeq.current) {
                    return;
                }
                const data = res.data?.podExemptSpotTaskDetail;
                if (!data?.task) {
                    setDetail(undefined);
                    return;
                }
                setDetail({
                    task: toSpotTaskRow(data.task),
                    samples: (data.samples ?? []).map(toSpotSampleRow),
                });
            } catch (e) {
                if (seq !== requestSeq.current) {
                    return;
                }
                setDetail(undefined);
                message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.loadDetailFailed'));
            } finally {
                if (seq === requestSeq.current) {
                    setLoading(false);
                }
            }
        },
        [client],
    );

    const clearDetail = useCallback(() => setDetail(undefined), []);

    return {detail, loading, loadDetail, clearDetail};
};
